import React from 'react';
import { useProcurement } from '@/context/ProcurementContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Building2, 
  FileText, 
  Calendar, 
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Download,
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  X
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

interface VendorInvoiceDetailsProps {
  invoice: any;
  onBack: () => void;
  onClose?: () => void;
  onVerify?: (invoiceId: number) => void;
  onPay?: (invoiceId: number) => void;
}

export const VendorInvoiceDetails: React.FC<VendorInvoiceDetailsProps> = ({ 
  invoice, 
  onBack, 
  onClose,
  onVerify, 
  onPay 
}) => {
  const { state } = useProcurement();

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'verified':
        return 'default';
      case 'paid':
        return 'default';
      case 'overdue':
        return 'destructive';
      case 'cancelled':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
      case 'paid':
        return <CheckCircle className="h-4 w-4" />;
      case 'overdue':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getVendorName = (vendorId: number) => {
    const vendor = state.vendors.find(v => v.id === vendorId);
    return vendor?.name || `Vendor ${vendorId}`;
  };

  const getVendorDetails = (vendorId: number) => {
    const vendor = state.vendors.find(v => v.id === vendorId);
    return vendor;
  };

  const getPONumber = (poId: number) => {
    const po = state.purchaseOrders.find(p => p.id === poId);
    return po?.poNumber || `PO-${poId}`;
  };

  const getPODetails = (poId: number) => {
    const po = state.purchaseOrders.find(p => p.id === poId);
    return po;
  };

  const vendor = getVendorDetails(invoice.vendorId);
  const po = getPODetails(invoice.poId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{invoice.invoiceNumber}</h1>
            <p className="text-muted-foreground">Vendor Invoice Details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getStatusVariant(invoice.status)} className="flex items-center gap-1">
            {getStatusIcon(invoice.status)}
            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
          </Badge>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          {onClose && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0 hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Invoice Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Invoice Number</p>
                  <p className="font-medium">{invoice.invoiceNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Invoice Date</p>
                  <p className="font-medium">
                    {invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Due Date</p>
                  <p className="font-medium">
                    {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Payment Terms</p>
                  <p className="font-medium">{invoice.paymentTerms || 'N/A'}</p>
                </div>
              </div>
              
              {invoice.notes && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Notes</p>
                    <p className="text-sm">{invoice.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle>Line Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {invoice.lineItems?.map((item: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{item.description}</p>
                      <p className="text-sm text-muted-foreground">
                        Quantity: {item.quantity} × {formatCurrency(item.unitPrice)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(item.totalPrice)}</p>
                    </div>
                  </div>
                ))}
                
                <Separator />
                
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>Total Amount:</span>
                  <span>{formatCurrency(invoice.totalAmount)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Related PO Information */}
          {po && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Related Purchase Order
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">PO Number</p>
                    <p className="font-medium">{po.poNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">PO Status</p>
                    <Badge variant="outline">{po.status}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">PO Date</p>
                    <p className="font-medium">
                      {new Date(po.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">PO Total</p>
                    <p className="font-medium">{formatCurrency(po.totalAmount)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Vendor Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Vendor Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-medium">{vendor?.name}</p>
                <p className="text-sm text-muted-foreground">{vendor?.category}</p>
              </div>
              
              {vendor?.contactPerson && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{vendor.contactPerson}</span>
                </div>
              )}
              
              {vendor?.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{vendor.email}</span>
                </div>
              )}
              
              {vendor?.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{vendor.phone}</span>
                </div>
              )}
              
              {vendor?.address && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span className="text-sm">{vendor.address}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {invoice.status === 'pending' && onVerify && (
                <Button
                  onClick={() => onVerify(invoice.id)}
                  className="w-full flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Verify Invoice
                </Button>
              )}
              
              {invoice.status === 'verified' && onPay && (
                <Button
                  onClick={() => onPay(invoice.id)}
                  className="w-full flex items-center gap-2"
                >
                  <DollarSign className="h-4 w-4" />
                  Mark as Paid
                </Button>
              )}
              
              <Button variant="outline" className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              
              <Button variant="outline" className="w-full">
                <FileText className="h-4 w-4 mr-2" />
                View PO Details
              </Button>
            </CardContent>
          </Card>

          {/* Payment Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-2xl font-bold">{formatCurrency(invoice.totalAmount)}</p>
              </div>
              
              {invoice.paidDate && (
                <div>
                  <p className="text-sm text-muted-foreground">Paid Date</p>
                  <p className="font-medium">
                    {new Date(invoice.paidDate).toLocaleDateString()}
                  </p>
                </div>
              )}
              
              {invoice.verifiedDate && (
                <div>
                  <p className="text-sm text-muted-foreground">Verified Date</p>
                  <p className="font-medium">
                    {new Date(invoice.verifiedDate).toLocaleDateString()}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

